import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Dimmer, Grid, Loader } from "semantic-ui-react";

import { useGetCountries, useGetCountryCurrency } from "../../../api/static-data";
import { CommonModal, DropDown, InputText, NewImageUploader, TitleView } from "../../../components";

import {
  useDeleteCompanyLogo,
  useGetCompanyById,
  useGetCompanyLogoSignedUrl,
  useUpdateCompanyData,
  useUploadCompanyImage
} from "../../../api/company";
import { InputDatePicker } from "../../../components/InputDatePicker/InputDatePicker";
import { InputNumber } from "../../../components/InputText/InputNumber";
import MainBottomButtonView from "../../../components/mainBottomButtonView/MainBottomButtonView";
import CommonSplitContainer from "../../../components/commonSplitContainer/CommonSplitContainer";
import { successMessage } from "../../../helpers/ErrorHandler";
import "./companyEdit.scss";

const CompanyEditModal = ({ visibleModal, setVisibleModal, id }: any) => {
  const [startDate, setStartDate] = useState(new Date());
  const [imageData, setImageData] = useState<File | null>(null);

  //custom hook for get company data by id
  const {
    data: companyDetails,
    isLoading: companyLoading,
    mutate,
    reset: getCompanyReset
  } = useGetCompanyById();

  const [country, setCountry] = useState<any>(companyDetails?.tenantCountry);
  const [currency, setCurrency] = useState<any>(companyDetails?.currency?._id);

  const {
    isLoading,
    isSuccess = false,
    mutate: updateCompanyData,
    reset: companyUpdateReset
  } = useUpdateCompanyData();

  const { isLoading: isLoadingCompanyImage, mutate: uploadCompanyImage } = useUploadCompanyImage();

  const { mutate: deleteCompanyLogo, isLoading: isDeletingLogo } = useDeleteCompanyLogo();

  // Fetch signed URL for company logo
  const {
    data: logoSignedUrlData,
    status: formFetchStatus,
    fetchStatus: formFetchState
  } = useGetCompanyLogoSignedUrl(companyDetails?.logoName);

  //company input data changes
  const values = useMemo(() => {
    setStartDate(companyDetails?.dateOfSubscription || "");
    return {
      name: companyDetails?.name || "",
      address: companyDetails?.address || "",
      companyEmail: companyDetails?.companyEmail || "",
      noOfEmployees: companyDetails?.noOfEmployees || "",
      phone: companyDetails?.phone || "",
      tenantType: companyDetails?.tenantType || "Tenant",
      tenantCountry: companyDetails?.tenantCountry || "",

      zipCode: companyDetails?.zipCode || "",
      noOfProducts: companyDetails?.noOfProducts || "",
      registrationNumber: companyDetails?.registrationNumber || "",
      website: companyDetails?.website || "",
      noOfUsers: companyDetails?.noOfUsers || 1,
      noOfSKUS: companyDetails?.noOfSKUS || 1
    };
  }, [companyDetails]);

  // Removed - handling success in onSubmit callback

  useEffect(() => {
    setCountry(companyDetails?.tenantCountry);
    setCurrency(companyDetails?.currency?._id);
  }, [companyDetails]);

  useEffect(() => {
    if (visibleModal) {
      mutate(id);
    }
  }, [visibleModal]);

  //custom hook company reset data
  useEffect(() => {
    return () => {
      reset();
      getCompanyReset();
      companyUpdateReset();
    };
  }, [visibleModal]);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors }
  } = useForm({
    defaultValues: values,
    values: values
  });

  const {
    data: countriesData,
    isSuccess: isCountriesDataSuccess,
    isLoading: isCountriesDataLoading
  } = useGetCountries();

  const { data: countryCurrencyData, isLoading: isCountryCurrencyDataLoading } =
    useGetCountryCurrency();

  //company data submit
  const onSubmit = async (data: any) => {
    data.name = data.name.trim();
    data.status = "ACTIVE";
    data.isParent = false;
    data.tenantCountry = country;
    data.tenantType = "Tenant";
    data.dateOfSubscription = startDate;
    data.currency = currency;

    updateCompanyData(
      { id: companyDetails?._id, data },
      {
        onSuccess: () => {
          // Upload image if changed
          if (imageData && companyDetails?._id) {
            uploadCompanyImage(
              {
                company_id: companyDetails._id,
                file: imageData,
                customFileName: companyDetails._id
              },
              {
                onSuccess: () => {
                  successMessage("Company data and logo updated successfully");
                  mutate(id);
                  setVisibleModal(false);
                },
                onError: () => {
                  successMessage("Company data updated successfully (logo upload failed)");
                  mutate(id);
                  setVisibleModal(false);
                }
              }
            );
          } else {
            successMessage("Company data updated successfully");
            mutate(id);
            setVisibleModal(false);
          }
        }
      }
    );
  };

  const countries = useMemo(() => {
    if (isCountriesDataSuccess) {
      return countriesData?.map((c: any) => ({
        key: c.id,
        text: c.country,
        value: c.id
      }));
    }
  }, [countriesData, isCountriesDataSuccess]);

  //country currency data
  const countryCurrency = useMemo(() => {
    return countryCurrencyData?.map((c: any) => ({
      key: c._id,
      text: `${c.country} - ${c.currency} - ${c.alphabeticCode}`,
      value: c._id
    }));
  }, [countryCurrencyData]);

  const loadingCompanyLogo = useMemo(() => {
    return formFetchStatus === "loading" && formFetchState === "fetching";
  }, [formFetchStatus, formFetchState]);

  //handle loading
  if (
    isLoading ||
    companyLoading ||
    isLoadingCompanyImage ||
    loadingCompanyLogo ||
    isDeletingLogo
  ) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  const deleteCompanyLogoHandler = () => {
    setImageData(null);
    if (companyDetails?._id) {
      deleteCompanyLogo(companyDetails._id, {
        onSuccess: () => {
          successMessage("Company logo deleted successfully");
          mutate(id); // Refresh company details
        },
        onError: () => {
          successMessage("Failed to delete logo");
        }
      });
    }
  };

  return (
    <>
      <CommonModal
        modalOpenState={visibleModal}
        modalCloseState={() => {
          setVisibleModal(false);
          reset();
        }}
        centered={true}
        title="Company Details"
      >
        <Grid>
          <Grid.Column computer={16} tablet={16} mobile={16}>
            <>
              <form onSubmit={handleSubmit(onSubmit)}>
                <CommonSplitContainer
                  topChild={
                    <Grid>
                      <Grid.Column computer={16} tablet={16} mobile={16}>
                        <TitleView
                          title="Company info"
                          CustomTitleViewMain="productRegisterTitle pl-0"
                        />
                        <Grid>
                          <Grid.Column
                            computer={8}
                            tablet={16}
                            mobile={16}
                            className="paddingRemoveBottom"
                          >
                            <InputText
                              register={register}
                              errors={errors.name}
                              labelName={"Company Name"}
                              placeholder="Company Name"
                              name="name"
                              required={true}
                              errorMessage={"Please enter user name"}
                            />
                          </Grid.Column>
                          <Grid.Column
                            computer={8}
                            tablet={16}
                            mobile={16}
                            className="paddingRemoveBottom"
                          >
                            <InputText
                              register={register}
                              errors={errors.address}
                              labelName={"Address"}
                              placeholder="Address"
                              name="address"
                              required={true}
                              errorMessage={"Please enter user name"}
                            />
                          </Grid.Column>

                          <Grid.Column
                            computer={8}
                            tablet={16}
                            mobile={16}
                            className="paddingRemoveTop tw-pb-0"
                          >
                            <InputText
                              register={register}
                              errors={errors.companyEmail}
                              labelName={"Company Email"}
                              placeholder="Company Email"
                              name="companyEmail"
                              required={true}
                            />
                          </Grid.Column>
                          <Grid.Column
                            computer={8}
                            tablet={16}
                            mobile={16}
                            className="paddingRemoveTop tw-pb-0"
                          >
                            <InputText
                              register={register}
                              errors={errors.phone}
                              labelName={"Phone"}
                              placeholder="Phone"
                              name="phone"
                              required={false}
                              errorMessage={"Please enter name"}
                            />
                          </Grid.Column>
                          <Grid.Column
                            computer={8}
                            tablet={16}
                            mobile={16}
                            className="paddingRemoveTop tw-pb-0"
                          >
                            <InputText
                              register={register}
                              errors={errors.phone}
                              labelName={"Registration Number"}
                              placeholder="Registration Number"
                              name="registrationNumber"
                              required={false}
                              errorMessage={"Please enter name"}
                            />
                          </Grid.Column>
                          <Grid.Column
                            computer={8}
                            tablet={16}
                            mobile={16}
                            className="paddingRemoveTop tw-pb-0"
                          >
                            <DropDown
                              labelName="Currency"
                              placeholder="Select currency"
                              defaultValue={currency}
                              currentData={countryCurrency}
                              handleChangeState={(e: any, { value }: any) => {
                                setCurrency(value);
                              }}
                              loading={isCountryCurrencyDataLoading}
                              search
                              key={"tenantCurrency"}
                            />
                          </Grid.Column>
                          <Grid.Column
                            computer={8}
                            tablet={16}
                            mobile={16}
                            className="paddingRemoveTop tw-pb-0"
                          >
                            <InputText
                              register={register}
                              errors={errors.phone}
                              labelName={"Website"}
                              placeholder="Website"
                              name="website"
                              required={false}
                              errorMessage={"Please enter name"}
                            />
                          </Grid.Column>

                          <Grid.Column
                            computer={8}
                            tablet={16}
                            mobile={16}
                            className="paddingRemoveTop tw-pb-0"
                          >
                            <InputNumber
                              transform={{
                                input: (value: any) =>
                                  isNaN(value) || value === 0 ? "" : value.toString(),
                                output: (e: any) => {
                                  return e;
                                }
                              }}
                              control={control}
                              errors={errors.noOfEmployees}
                              name="noOfEmployees"
                              labelName={"No Of Employees"}
                            />
                          </Grid.Column>
                          <Grid.Column
                            computer={8}
                            tablet={16}
                            mobile={16}
                            className="paddingRemoveTop tw-pb-0"
                          >
                            <InputNumber
                              defaultValue={1}
                              transform={{
                                input: (value: any) =>
                                  isNaN(value) || value === 0 ? "" : value.toString(),
                                output: (e: any) => {
                                  return e;
                                }
                              }}
                              control={control}
                              errors={errors.noOfEmployees}
                              name="noOfUsers"
                              labelName={"No Of Users"}
                              removeDecimal={true}
                            />
                          </Grid.Column>
                          <Grid.Column
                            computer={8}
                            tablet={16}
                            mobile={16}
                            className="paddingRemoveTop tw-pb-0"
                          >
                            <InputNumber
                              transform={{
                                input: (value: any) =>
                                  isNaN(value) || value === 0 ? "" : value.toString(),
                                output: (e: any) => {
                                  return e;
                                }
                              }}
                              control={control}
                              errors={errors.noOfProducts}
                              name="noOfProducts"
                              labelName={"No Of Products"}
                            />
                          </Grid.Column>
                          <Grid.Column
                            computer={8}
                            tablet={16}
                            mobile={16}
                            className="paddingRemoveTop tw-pb-0"
                          >
                            <InputNumber
                              defaultValue={1}
                              transform={{
                                input: (value: any) =>
                                  isNaN(value) || value === 0 ? "" : value.toString(),
                                output: (e: any) => {
                                  return e;
                                }
                              }}
                              control={control}
                              errors={errors.noOfProducts}
                              name="noOfSKUS"
                              labelName={"No Of SKUS"}
                              removeDecimal={true}
                            />
                          </Grid.Column>
                          <Grid.Column
                            computer={8}
                            tablet={16}
                            mobile={16}
                            className="paddingRemoveTop tw-pb-0"
                          >
                            <InputText
                              register={register}
                              errors={errors.phone}
                              labelName={"Industry"}
                              placeholder="Industry"
                              name="industry"
                            />
                          </Grid.Column>
                          <Grid.Column
                            computer={8}
                            tablet={16}
                            mobile={16}
                            className="paddingRemoveTop tw-pb-0"
                          >
                            <DropDown
                              labelName="Country"
                              placeholder="Select country"
                              defaultValue={country}
                              currentData={countries}
                              handleChangeState={(e: any, { value }: any) => {
                                setCountry(value);
                              }}
                              loading={isCountriesDataLoading}
                              search
                              key={"tenantCountry"}
                            />
                          </Grid.Column>

                          <Grid.Column
                            computer={8}
                            tablet={16}
                            mobile={16}
                            className="paddingRemoveTop tw-pb-0"
                          >
                            <InputText
                              register={register}
                              errors={errors.zipCode}
                              labelName={"zipCode"}
                              placeholder="zipCode"
                              name="zipCode"
                            />
                          </Grid.Column>
                          <Grid.Column
                            computer={8}
                            tablet={16}
                            mobile={16}
                            className="paddingRemoveTop tw-pb-0"
                          >
                            <InputDatePicker
                              labelName="Date of Subscription"
                              changeDate={setStartDate}
                              currentSelectedData={startDate}
                            />
                          </Grid.Column>
                          <Grid.Column
                            computer={8}
                            tablet={16}
                            mobile={16}
                            className="paddingRemoveTop tw-pb-0"
                          >
                            {loadingCompanyLogo ? (
                              <div>
                                <p className="labelUploder">Company Logo</p>
                                <Loader active inline="centered" size="small">
                                  Loading logo...
                                </Loader>
                              </div>
                            ) : (
                              <NewImageUploader
                                mainTitle="Company Logo"
                                buttonTitle="Upload Logo"
                                defaultImage={logoSignedUrlData?.data}
                                imageData={(file: File | null) => setImageData(file)}
                                deleteImage={() => deleteCompanyLogoHandler()}
                                convertToPng
                                testId="company-logo-uploader"
                              />
                            )}
                          </Grid.Column>
                        </Grid>
                      </Grid.Column>
                    </Grid>
                  }
                  bottomChild={
                    <MainBottomButtonView
                      cancelButtonStatus
                      saveButtonStatus
                      cancelButton={() => {
                        setVisibleModal(false);
                        reset();
                      }}
                      saveButton={handleSubmit(onSubmit)}
                    />
                  }
                />
              </form>
            </>
          </Grid.Column>
        </Grid>
      </CommonModal>
    </>
  );
};

export default CompanyEditModal;
